import React from 'react';
import { css } from '@emotion/css';
import { Box, BoxProps } from '@spark-web/box';
import { Button, ButtonProps } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Inline, InlineProps } from '@spark-web/inline';
import { Spinner } from '@spark-web/spinner';
import { Stack, StackProps } from '@spark-web/stack';
import { Text, TextProps } from '@spark-web/text';

interface SelectionCardProps extends BoxProps {
  isLoading?: boolean;
  className?: string;
}

const SelectionCard: React.FC<SelectionCardProps> = React.forwardRef<HTMLDivElement, SelectionCardProps>(
  ({ isLoading, children, ...props }, ref) => {
    return (
      <Box
        ref={ref}
        display="flex"
        borderWidth="standard"
        border="standard"
        borderRadius="medium"
        padding="large"
        justifyContent="stretch"
        alignItems="start"
        alignSelf="stretch"
        background="neutral"
        gap="large"
        position="relative"
        {...props}
      >
        {isLoading && (
          <Box
            position="absolute"
            top={0}
            right={0}
            left={0}
            bottom={0}
            width="full"
            height="full"
            display="flex"
            justifyContent="center"
            alignItems="center"
            background="backdrop"
            data-testid="selection-card-loader"
          >
            <Spinner size="xsmall" />
          </Box>
        )}
        {children}
      </Box>
    );
  },
);

SelectionCard.displayName = 'SelectionCard';

const SelectionCardContent: React.FC<StackProps> = React.forwardRef<HTMLDivElement, StackProps>((props, ref) => {
  return <Stack gap="large" flex={1} ref={ref} {...props} />;
});

SelectionCardContent.displayName = 'SelectionCard.Content';

const SelectionCardTitle = React.forwardRef<HTMLHeadingElement, TextProps>((props, ref) => {
  return <Text ref={ref} weight="semibold" size="standard" {...props} />;
});

SelectionCardTitle.displayName = 'SelectionCard.Title';

const SelectionCardIcon: React.FC<InlineProps> = React.forwardRef<HTMLDivElement, InlineProps>((props, ref) => {
  return <Inline gap="small" ref={ref} {...props} />;
});

SelectionCardIcon.displayName = 'SelectionCard.Icon';

const SelectionCardText = React.forwardRef<HTMLDivElement, TextProps>((props, ref) => {
  return <Text ref={ref} size="small" {...props} />;
});

SelectionCardText.displayName = 'SelectionCard.Text';

const SelectionCardTextBanner: React.FC<InlineProps> = React.forwardRef<HTMLDivElement, InlineProps>((props, ref) => {
  return (
    <Inline
      ref={ref}
      gap="small"
      alignY="center"
      background="positiveLow"
      borderRadius="medium"
      width="full"
      padding="medium"
      className={css({
        flexWrap: 'nowrap',
      })}
      {...props}
    />
  );
});

SelectionCardTextBanner.displayName = 'SelectionCard.Banner';

const SelectionCardActions = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  return <Button ref={ref} tone="primary" type="button" size="medium" {...props} />;
});

SelectionCardActions.displayName = 'SelectionCard.Actions';

type SelectionCardFooterProps = TextProps & {
  onClick?: () => void;
  disabled?: boolean;
};
const SelectionCardFooter = React.forwardRef<HTMLDivElement, SelectionCardFooterProps>(
  ({ onClick, disabled, ...props }, ref) => {
    return (
      <Stack gap="large">
        <Divider />
        <Text
          ref={ref}
          size="small"
          tone="link"
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => {
            if (!disabled) {
              onClick?.();
            }
          }}
          {...props}
        />
      </Stack>
    );
  },
);

SelectionCardFooter.displayName = 'SelectionCard.Footer';

type SelectionConfirmationButtonProps = ButtonProps & { active?: boolean };

const SelectionConfirmationButton = React.forwardRef<HTMLButtonElement, SelectionConfirmationButtonProps>(
  ({ active, ...props }, ref) => {
    if (active) {
      return <Button ref={ref} prominence="low" tone="primary" type="button" size="medium" {...props} />;
    }
    return <Button ref={ref} tone="neutral" type="button" size="medium" {...props} />;
  },
);

SelectionConfirmationButton.displayName = 'SelectionCard.ConfirmationButton';

export {
  SelectionCard,
  SelectionCardContent,
  SelectionCardTitle,
  SelectionCardIcon,
  SelectionCardText,
  SelectionCardActions,
  SelectionCardTextBanner,
  SelectionCardFooter,
  SelectionConfirmationButton,
};
